import { Fragment } from 'react';
import {
  CardCost,
  CardFree,
  CardMeta,
  CardRow,
  CardRows,
} from '@/components/agent-chat/card-parts.component';
import { CreditAmount } from '@/components/ui/chips/credit-amount';
import type { ReviewCard } from '@/types/gtm-agents';
import {
  reviewCardChanges,
  reviewCardItemCost,
  reviewCardTotalCosts,
  type ReviewCardChangeLine,
} from './review-card.util';

/** A raise's words, a credit amount as the coin and the number. */
function ChangeLine({ line }: { line: ReviewCardChangeLine }) {
  if (!line.credits) return <>{line.text}</>;
  const { now, was } = line.credits;
  return (
    <>
      {line.text}
      {now !== null ? (
        <>
          {' '}
          <CreditAmount amount={now} />
        </>
      ) : null}
      {was !== null ? (
        <>
          {', was '}
          <CreditAmount amount={was} />
        </>
      ) : null}
    </>
  );
}

/** A step's raises against the saved play, a gray line each under its row
 *  (the play change card and a one-off's Confirm both show them). */
export function ReviewCardChangeList({
  label,
  changes,
}: {
  label: string;
  changes: ReviewCardChangeLine[];
}) {
  if (!changes.length) return null;
  return (
    <ul
      aria-label={`${label} changes`}
      className='flex flex-col px-[10px] text-[12px] leading-[16px] text-muted-foreground'
    >
      {changes.map((line, index) => (
        <li key={`${index}-${line.text}`}>
          <ChangeLine line={line} />
        </li>
      ))}
    </ul>
  );
}

/**
 * The review card (consent split): every step the proposal adds or changes
 * that spends credits or reaches outside the account, confirmed once by
 * Accept (a one-off's card: by Confirm), a row each with its most per record
 * or run, or Free. Hygiene steps (exclusions, dedupe, validation) aren't
 * asked about. Each raise against the saved play (more records, error
 * handling, owners, overwrites, run limits) is its own line under its step,
 * and the card's credit totals, when it has them, close it.
 */
export function ReviewCardList({
  card,
  heading,
}: {
  card: Pick<ReviewCard, 'items' | 'totals'>;
  heading?: string;
}) {
  const totals = reviewCardTotalCosts(card.totals);
  return (
    <section
      aria-label='Review card'
      data-review-card
      className='flex flex-col gap-[4px]'
    >
      {heading ? <CardMeta>{heading}</CardMeta> : null}
      <CardRows>
        {card.items.map((item) => {
          const cost = reviewCardItemCost(item);
          const changes = reviewCardChanges(item);
          return (
            <Fragment key={item.nodeId}>
              <CardRow
                label={<span title={item.label}>{item.label}</span>}
                end={
                  cost === 'free' ? (
                    <CardFree />
                  ) : cost ? (
                    <CardCost upTo amount={cost.amount} unit={cost.unit} />
                  ) : undefined
                }
              />
              <ReviewCardChangeList label={item.label} changes={changes} />
            </Fragment>
          );
        })}
      </CardRows>
      {totals.length ? (
        <p
          data-review-card-totals
          className='flex flex-wrap items-center gap-x-[8px]'
        >
          {totals.map((cost) => (
            <CardCost
              key={cost.unit}
              upTo
              amount={cost.amount}
              unit={cost.unit}
            />
          ))}
        </p>
      ) : null}
    </section>
  );
}
